import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faTimes,
  faPlayCircle,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import './TikTokAnalysis.css';

const TikTokAnalysis = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFindings, setExpandedFindings] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const csvResponse = await fetch('/data/top_50_final_analysis.csv');
        const csvData = await csvResponse.text();

        Papa.parse(csvData, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const parsedData = results.data.map((row, index) => {
              let analysis = {};
              try {
                let rawAnalysis = row['LLM Analysis'] || '{}';
                if (rawAnalysis.startsWith('"') && rawAnalysis.endsWith('"')) {
                  rawAnalysis = rawAnalysis.slice(1, -1);
                }
                const cleanJson = rawAnalysis.replace(/""/g, '"');
                analysis = JSON.parse(cleanJson);
              } catch (e) {
                console.error('Error parsing LLM Analysis at index', index, e);
                try {
                  analysis = JSON.parse(row['LLM Analysis']);
                } catch (e2) { }
              }

              // Direct TikTok thumbnails usually fail due to CORS/Referer
              // We'll use a high-quality stylized placeholder that looks like a video frame
              const thumbnail = `https://ui-avatars.com/api/?name=${encodeURIComponent(row.Author || 'TIK')}&background=random&color=fff&size=512&bold=true`;

              return { ...row, analysis, id: index, thumbnail };
            });
            setVideos(parsedData);
            setLoading(false);
          },
          error: (error) => {
            console.error('Error parsing CSV:', error);
            setLoading(false);
          }
        });
      } catch (error) {
        console.error('Error loading CSV file:', error);
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const toggleFinding = (idx) => {
    setExpandedFindings(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const isTikTokUrl = (url) => {
    if (!url) return false;
    return url.includes('tiktok.com');
  };

  const getTikTokEmbedUrl = (url) => {
    if (!url) return '';
    // Extract video ID from various TikTok URL formats
    const videoIdMatch = url.match(/\/video\/(\d+)/) || url.match(/v=(\d+)/) || url.match(/\/v\/(\d+)/);
    const videoId = videoIdMatch ? videoIdMatch[1] : '';
    
    if (videoId) {
      return `https://www.tiktok.com/embed/v2/${videoId}?lang=en-US`;
    }
    return url;
  };

  const filteredVideos = videos.filter(v =>
    (v.Author?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (v.Transcript?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (v.analysis?.productNames?.join(' ')?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const openVideo = (video) => {
    setSelectedVideo(video);
    setExpandedFindings({}); // Reset when opening new video
  };

  const closeVideo = () => {
    setSelectedVideo(null);
  };

  if (loading) {
    return (
      <div className="tiktok-loading">
        <div className="loader"></div>
        <p>Analyzing TikTok Data...</p>
      </div>
    );
  }

  return (
    <div className="tiktok-analysis-container">
      {!selectedVideo ? (
        <div className="master-view">
          <header className="tiktok-header">
            <div className="header-content">
              <h1>TikTok Pattern Reviews</h1>
            </div>
            <div className="search-bar">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                placeholder="Search authors, products, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </header>

          <div className="video-grid">
            {filteredVideos.map((video) => (
              <div key={video.id} className="video-card" onClick={() => openVideo(video)}>
                <div className="video-thumbnail-container">
                  <div className="video-preview-iframe-wrapper">
                    <iframe
                      src={getTikTokEmbedUrl(`https://www.tiktok.com/video/${video['Video ID']}`)}
                      title={`Preview @${video.Author}`}
                      scrolling="no"
                      className="card-preview-iframe"
                    />
                  </div>
                  <div className="play-overlay">
                    <FontAwesomeIcon icon={faPlayCircle} />
                  </div>
                  <div className="pattern-count">
                    {video.analysis?.darkPatternAnalysis?.length || 0} Patterns
                  </div>
                </div>
                <div className="video-info">
                  <h3>@{video.Author}</h3>
                  <div className="stats">
                    <span>{video.Likes} Likes</span> • <span>{video.Plays} Plays</span>
                  </div>
                  <div className="confidence-score">
                    Confidence: {video.Confidence}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="detail-view-full">
          <div className="detail-nav">
            <button className="back-btn" onClick={closeVideo}>
              <FontAwesomeIcon icon={faTimes} /> Back to Dashboard
            </button>
            <div className="video-title-header">
              <h2>Video Review: @{selectedVideo.Author}</h2>
              <div className="quick-stats">
                <span>ID: {selectedVideo['Video ID']}</span>
                <span>Likes: {selectedVideo.Likes}</span>
                <span>Plays: {selectedVideo.Plays}</span>
              </div>
            </div>
          </div>

          <div className="detail-layout">
            <div className="left-panel">
              <div className="video-embed-box">
                <iframe
                  src={`https://www.tiktok.com/embed/v2/${selectedVideo['Video ID']}?lang=en-US`}
                  title="TikTok Video Preview"
                  allowFullScreen
                />
              </div>
              <div className="transcript-panel">
                <h3>Video Transcript</h3>
                <p>{selectedVideo.Transcript}</p>

                {selectedVideo.analysis?.productNames?.length > 0 && (
                  <div className="product-list-panel">
                    <h4>Detected Products</h4>
                    <div className="tag-container">
                      {selectedVideo.analysis.productNames.map((p, i) => (
                        <span key={i} className="product-pill">{p}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="right-panel">
              <div className="analysis-summary-section">
                <div className="summary-pill">
                  <span className="lbl">Identified Patterns</span>
                  <span className="val">{selectedVideo.analysis?.darkPatternAnalysis?.length || 0}</span>
                </div>
                <div className="summary-pill blue">
                  <span className="lbl">Overall Confidence</span>
                  <span className="val">{selectedVideo.analysis?.overallConfidenceScore || selectedVideo.Confidence}%</span>
                </div>
              </div>

              <div className="pattern-details-section">
                <h3>Detailed Findings</h3>
                {selectedVideo.analysis?.darkPatternAnalysis?.map((pattern, idx) => (
                  <div key={idx} className={`finding-card ${expandedFindings[idx] ? 'expanded' : 'collapsed'}`}>
                    <div className="finding-top" onClick={() => toggleFinding(idx)}>
                      <div className="finding-title-group">
                        <span className="finding-title">{pattern.category}</span>
                        <span className="finding-conf">{pattern.confidenceScore}% Certainty</span>
                      </div>
                      <div className="toggle-icon">
                        {expandedFindings[idx] ? '-' : '+'}
                      </div>
                    </div>

                    {expandedFindings[idx] && (
                      <div className="finding-content">
                        {pattern.excerpt && (
                          <div className="finding-quote">
                            "{pattern.excerpt}"
                          </div>
                        )}

                        <p className="finding-text">{pattern.reasoning}</p>

                        {pattern.regulatoryViolationReference?.length > 0 && (
                          <div className="law-references">
                            <div className="law-title">
                              <FontAwesomeIcon icon={faShieldAlt} /> Legal Analysis
                            </div>
                            {pattern.regulatoryViolationReference.map((ref, ridx) => (
                              <div key={ridx} className="law-card">
                                <div className="law-name">{ref.lawGuidance} • {ref.articleClause}</div>
                                <div className="law-summary">{ref.highLevelSynthesis}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {(!selectedVideo.analysis?.darkPatternAnalysis || selectedVideo.analysis.darkPatternAnalysis.length === 0) && (
                  <div className="no-findings">
                    <FontAwesomeIcon icon={faShieldAlt} size="4x" />
                    <p>Compliance Check: No dark patterns identified in this automated scan.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TikTokAnalysis;
